import { createContext, type ComponentType, type RefObject } from 'react';

import type { FaseAccettata } from '@/lib/limulo/sfideTab';
import type { LimuloChallengeWithIdentities } from '@/types/limulo';

/**
 * GLI ATTRIBUTI DELLA RIGA CHE IL DEEP LINK `sfida=<id>` SEGNA (`useRigaEvidenziata` di `SfideDiPiattaformaTab`, privata): il riferimento per il fuoco e lo scorrimento, `aria-current`, `tabIndex` e il marcatore. Una pelle li stende sul SUO `<li>`:
 * senza, il link non evidenzia nessuna riga e un lettore di schermo tace.
 */
export interface SegnoRiga {
  ref: RefObject<HTMLLIElement | null>;
  'aria-current': 'true' | undefined;
  tabIndex: number | undefined;
  'data-evidenza': string | undefined;
}

/**
 * LA VISTA DI UNA SFIDA RICEVUTA che `RigaInArrivo` passa a una riga alternativa, quando qualcuno sopra di lei ne ha messa una nel Context. Porta SOLO valori che la riga ha gia' calcolato e i gesti di oggi: nessun verdetto nuovo, nessuna mutazione.
 *
 * @param sfida la sfida come la manda il server (lo stato e' suo: nessun orologio del telefono lo decide).
 * @param nome chi sfida (`nomeDiChiSfida`, col ripiego di oggi).
 * @param nomeGioco il nome del gioco dal catalogo.
 * @param spento un tocco e' in volo, o la riga non e' piu' offerta: i due bottoni sono spenti.
 * @param esito l'esito positivo della risposta (la riga resta un attimo con la sua frase), o `null`.
 * @param testoErrore la frase d'errore dell'ultimo tocco o della riga uscita, o `null`.
 * @param avvisoCoda «Se accetti esci dalla coda…» si mostra (`uscitaDallaCodaAccettando`, riga offerta e pulita).
 * @param idAvvisoCoda l'id dell'avviso, per `aria-describedby` di ACCETTA.
 * @param collegando l'accettazione aspetta il sync del profilo: si dice, invece di un bottone che gira muto.
 * @param onAccetta il gesto di oggi: la guardia contro il doppio tocco, la chiamata e la lettura dell'esito.
 * @param onRifiuta il gesto di oggi, con le sue stesse guardie.
 * @param segno gli attributi del deep link, da stendere sul `<li>`.
 * @param evidenziataClasse la cornice e il margine di scorrimento della riga in evidenza, o `undefined`.
 */
export interface VistaRigaRicevuta {
  sfida: LimuloChallengeWithIdentities;
  nome: string;
  nomeGioco: string;
  spento: boolean;
  esito: string | null;
  testoErrore: string | null;
  avvisoCoda: boolean;
  idAvvisoCoda: string;
  collegando: boolean;
  onAccetta: () => void;
  onRifiuta: () => void;
  segno: SegnoRiga;
  evidenziataClasse: string | undefined;
}

/** L'esito dell'ultimo tocco su «Annulla»: `ok` finche' la lista non ha riletto, `errore` con le parole di oggi. */
export interface NotaRigaInviata {
  tipo: 'ok' | 'errore';
  testo: string;
}

/**
 * LA VISTA DI UNA SFIDA INVIATA che `RigaInviata` passa a una riga alternativa. Anche qui solo valori gia' calcolati e i gesti di oggi.
 *
 * @param sfida la sfida come la manda il server: `state` e' lo stato EFFETTIVO, e `createdAt` puo' mancare.
 * @param nome l'altro (`nomeDellAltro`, col ripiego «Un amico»).
 * @param nomeGioco il nome del gioco dal catalogo.
 * @param fase per una sfida accettata, a che punto e' la partita (`faseDellaAccettata`); altrimenti `null`.
 * @param statoTesto la frase di stato di oggi («In attesa di risposta», «{{name}} ha accettato»…).
 * @param notaVisibile l'esito dell'annullamento ancora da mostrare, o `null`.
 * @param annullabile «Annulla» si offre (`staAnnullabile`): e' l'UNICO predicato da leggere per il bottone.
 * @param revocaInVolo la revoca e' in volo: il bottone e' spento.
 * @param annulla il gesto di oggi, intero: la guardia contro il doppio tocco, la revoca e la lettura dell'esito.
 * @param vaiAlTorneo il gesto di oggi: l'aptica e la rotta del torneo nato dall'accettazione.
 * @param segno gli attributi del deep link, da stendere sul `<li>`.
 * @param evidenziataClasse la cornice e il margine di scorrimento della riga in evidenza, o `undefined`.
 */
export interface VistaRigaInviata {
  sfida: LimuloChallengeWithIdentities;
  nome: string;
  nomeGioco: string;
  fase: FaseAccettata | null;
  statoTesto: string;
  notaVisibile: NotaRigaInviata | null;
  annullabile: boolean;
  revocaInVolo: boolean;
  annulla: () => void;
  vaiAlTorneo: () => void;
  segno: SegnoRiga;
  evidenziataClasse: string | undefined;
}

/**
 * LE PELLI ALTERNATIVE del tab «Sfide» di piattaforma: il titolo di ogni sezione e la riga delle ricevute e delle inviate. Ognuna e' facoltativa: senza, il tab rende il pezzo di sempre.
 * `conteggio` e' il numero delle sfide OFFERTE (le «vive»: non le righe che restano un attimo con l'esito), solo per le ricevute.
 */
export interface PelleSfide {
  Titolo?: ComponentType<{ sezione: 'ricevute' | 'inviate'; conteggio: number }>;
  RigaRicevuta?: ComponentType<{ vista: VistaRigaRicevuta }>;
  RigaInviata?: ComponentType<{ vista: VistaRigaInviata }>;
}

/**
 * IL PUNTO D'INNESTO delle righe e dei titoli del tab «Sfide» (stesso schema di `SlotPelleCode`). Il valore di partenza e' `null`: senza nessuno sopra, il tab e' quello di sempre e il DOM non cambia di un nodo.
 * ⛔ Il valore di partenza resta `null`. ⚠️ Il valore e' un oggetto DI MODULO, con componenti di modulo: uno creato nel render cambierebbe identita' a ogni giro (ogni lettura della lista) e smonterebbe le righe, con l'esito di un tocco appena fatto.
 */
export const SlotPelleSfide = createContext<PelleSfide | null>(null);
